import type { DocNode } from '@atlaskit/adf-schema';
import type { InputMethodInsertMedia } from '@atlaskit/editor-common/analytics';
import type {
	PublicPluginAPI,
	OptionalPlugin,
	_MarkdownModePluginStub,
} from '@atlaskit/editor-common/types';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import type { ExpandPlugin } from '@atlaskit/editor-plugin-expand';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type { MediaPlugin } from '@atlaskit/editor-plugin-media';
import type { Repair } from '@atlassian/adf-parser';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import type { ConfluenceCreatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-page-command';
import type { ConfluenceDeepResearchPageCreateCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-deep-research-page-create-command';
import type { ConfluencePageInitCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-page-init-command';
import type {
	ConfluenceUpdateImageOnPageData,
	ConfluenceUpdatePageCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import type {
	EditorInsertContentCommandProperties,
	EditorInsertContentPresentation,
} from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import type { CommandProperties } from '@atlassian/rovo-content-bridge-api/command';

import type { AdditionalContext, ContentLocation } from '../types/content';

export type StreamType = 'create' | 'deep-research-create' | 'update' | 'init';

export type StreamCommandProperties =
	| ConfluenceCreatePageCommandProperties
	| ConfluenceDeepResearchPageCreateCommandProperties
	| ConfluenceUpdatePageCommandProperties
	| ConfluencePageInitCommandProperties;

/**
 * Parameters passed to stream start callbacks.
 * Includes the stream type and required command properties when streaming begins.
 */
export type StreamStartCallbackParams = {
	payload: StreamCommandProperties;
	streamType: StreamType;
};

/**
 * Callback function invoked when a streaming operation begins.
 * This allows listeners to prepare for incoming stream data and set up any necessary state.
 *
 * @param params - Object containing the stream type and command properties
 */
export type StreamStartCallback = (params: StreamStartCallbackParams) => void;

/**
 * Parameters passed to stream callbacks during active streaming.
 * Contains the stream type and current command properties with streaming data.
 */
export type StreamCallbackParams = { payload: StreamCommandProperties; streamType: StreamType };

/**
 * Callback function invoked during active streaming operations.
 * Called repeatedly as stream data arrives to allow real-time processing and UI updates.
 *
 * @param params - Object containing the stream type and current command properties
 */
export type StreamCallback = (params: StreamCallbackParams) => void;

/**
 * Parameters passed to stream end callbacks when streaming completes.
 * Contains the stream type and command properties for cleanup operations.
 */
export type StreamEndCallbackParams = {
	adfDoc?: DocNode;
	payload: StreamCommandProperties;
	streamType: StreamType;
};

/**
 * Callback function invoked when a streaming operation completes successfully.
 * Used to finalize processing, clean up resources, and update the UI to reflect completion.
 *
 * @param params - Object containing the stream type and final command properties
 */
export type StreamEndCallback = (params: StreamEndCallbackParams) => void;

/**
 * Callback function invoked when a streaming operation is aborted.
 * Used to clean up resources and update the UI to reflect aborting.
 */
export type StreamAbortCallback = () => void;

/**
 * Callback function invoked when Rovo has finished reading and assembling
 * context for an outgoing message.
 */
export type SendContextReadyCallback = () => void;

/**
 * Parameters passed to stream timeout callbacks.
 * Contains the stream type for context.
 */
export type StreamTimeoutCallbackParams = { streamType: StreamType; traceId: string | undefined };

/**
 * Callback function invoked when a streaming operation times out.
 * Used to clean up resources and update the UI to reflect timing out.
 */
export type StreamTimeoutCallback = (params: StreamTimeoutCallbackParams) => void;

/**
 * Safe, shape-only debug metadata describing a single create-stream chunk and
 * the parser's accumulated buffer at the time a parse error was observed.
 *
 * IMPORTANT: This must only ever contain shape metadata — lengths, booleans,
 * the first structural (non-whitespace) token, fragment index, error type and
 * trace/action IDs. It MUST NOT contain raw ADF, HTML, prompt text, LLM
 * output, titles, body text, or arbitrary JSON snippets.
 *
 * Used to investigate Smart Create blank/empty page cases where the frontend
 * reports parser errors such as
 * `AdfUnexpectedDelimiterError: Expected LEFT_BRACE or STRING, got COMMA at position 0`.
 */
export type CreateStreamDebug = {
	accumulatedFirstNonWhitespaceCharBefore: string | null;
	accumulatedLengthBefore: number;
	accumulatedStartsWithDocEnvelopeBefore: boolean;
	accumulatedWasEmptyBefore: boolean;
	actionId?: string;
	chunkFirstNonWhitespaceChar: string | null;
	chunkIndex: number;
	chunkLength: number;
	chunkStartsWithComma: boolean;
	chunkStartsWithDocEnvelope: boolean;
	errorMessage?: string;
	errorName?: string;
	firstNonEmptyChunkFirstChar?: string | null;
	// --- First-chunk telemetry (v2) ---------------------------------------
	// Captures the very first non-empty chunk the FE received for this stream,
	// so we can tell whether the doc envelope (fragment 0) ever arrived or the
	// stream started mid-document (e.g. with a comma-prefixed continuation).
	firstNonEmptyChunkIndex?: number;
	firstNonEmptyChunkStartsWithComma?: boolean;
	firstNonEmptyChunkStartsWithDocEnvelope?: boolean;
	// --- First parser error across the whole stream (v2) ------------------
	firstParserErrorChunkIndex?: number;
	firstParserErrorMessage?: string;
	isFinished: boolean;
	parserPhase: 'stream-process-chunk' | 'finish-parse';

	previousAccumulatedDataFirstNonWhitespaceChar?: string | null;
	previousAccumulatedDataLength?: number;
	// --- previousAccumulatedData lifecycle (v2) ---------------------------
	// `Present` is signalled by the command handler when the payload carried
	// previousAccumulatedData; `Replayed` indicates the replay path actually
	// ran. Present-but-not-replayed points at the modal reopen/replay bug.
	previousAccumulatedDataPresent?: boolean;
	previousAccumulatedDataReplayed?: boolean;

	previousAccumulatedDataStartsWithDocEnvelope?: boolean;
	previousAccumulatedDataWasReplayed?: boolean;
	// --- Receiver/processor lifecycle (v2) --------------------------------
	processorWasInitializedWhenCommandArrived?: boolean;

	processorWasResetDuringStream?: boolean;
	receivedChunkCount?: number;

	sawCommaPrefixedChunkBeforeDocEnvelope?: boolean;
	// --- Whole-stream shape summary (v2) ----------------------------------
	sawDocEnvelopeChunk?: boolean;
	streamDebugVersion: 2;

	streamType: 'create';
	traceId?: string;
};

/**
 * Shape-only lifecycle signal recorded by the command handler when a create
 * command arrives, so the processor can attribute blank-page failures to
 * receiver/replay lifecycle issues. Contains no raw content.
 */
export type CreateCommandArrivalDebug = {
	previousAccumulatedDataPresent: boolean;
	previousAccumulatedDataStartsWithDocEnvelope?: boolean;
	processorWasInitializedWhenCommandArrived: boolean;
};

/**
 * Parameters passed to error callbacks when streaming operations fail.
 * Contains error information and the stream type for context.
 */
export type StreamErrorCallbackParams = {
	error: unknown;
	/**
	 * Optional shape-only debug metadata for create-stream parse failures.
	 * Only populated for the `create` stream type when the relevant feature
	 * gate is enabled. Backward compatible — consumers may ignore it.
	 */
	streamDebug?: CreateStreamDebug;
	streamType: StreamType;
	traceId: string | undefined;
};

/**
 * Callback function invoked when a streaming operation encounters an error.
 * Used to handle failures, display error messages, and clean up resources.
 *
 * @param params - Object containing the error and stream type for context
 */
export type StreamErrorCallback = (params: StreamErrorCallbackParams) => void;

/**
 * Parameters passed to repair callback when repairs are made during streaming.
 */
export type StreamRepairCallbackParams = { repairs: Repair[]; streamType: StreamType };

/**
 * Callback function invoked when repairs are made during streaming operations.
 */
export type StreamRepairCallback = (params: StreamRepairCallbackParams) => void;

/**
 * Optional metadata about the originating request, forwarded by the bridge handler so the
 * callback can attach correlation ids to its own analytics/error reporting. Bridge-driven
 * callers cannot rely on `useAnalyticsPropertiesContext` from the editor side because the
 * editor and chat trees are siblings in the host product, not nested.
 */
export type OnUploadAndInsertMediaMeta = {
	/**
	 * Aspect ratio (e.g. "16:9", "1:1") selected by the user in the
	 * `ImageFromAIGeneration` component. When present together with
	 * `inputMethod === INPUT_METHOD.REMIX_FROM_MEDIA_INSERTS`, the inserted media
	 * node is sized using the designer-tuned per-ratio container dimensions
	 * instead of the default editor layout width.
	 */
	aspectRatio?: string | null;
	conversationChannelId?: string;
	/** The input method to use for the document inserted analytics event. Defaults to INPUT_METHOD.REMIX. */
	inputMethod?: InputMethodInsertMedia;
	/** When true, shows the AI-generating overlay during upload. Defaults to no overlay. */
	isAIGenerated?: boolean;
	/** When present, use the positions for the insertion */
	positions?: [number, number];
};

export type OnUploadAndInsertMediaCallback = (
	sourceUrl: string,
	payload?: Partial<ConfluenceUpdateImageOnPageData>,
	presentation?: EditorInsertContentPresentation,
	meta?: OnUploadAndInsertMediaMeta,
) => Promise<undefined | { fieldId: string; type: 'create' | 'update' }>;

/**
 * Properties passed through to command handlers from the command manager.
 */
export type PassThroughCommandProps = {
	additionalContext?: AdditionalContext;
	contentId: string;
	contentLocation: ContentLocation;
	contentType: 'page';
	editorAPI: PublicPluginAPI<
		[
			AIPlugin,
			AiStreamingOrchestratorPlugin,
			OptionalPlugin<ExpandPlugin>,
			OptionalPlugin<LocalIdPlugin>,
			OptionalPlugin<MalleableUiPlugin>,
			OptionalPlugin<MediaPlugin>,
			OptionalPlugin<_MarkdownModePluginStub>,
			OptionalPlugin<CollabEditPlugin>,
		]
	>;
	onClearAIGenerating?: (previousMediaFileId: string) => void;
	onInsertContent?: (params: EditorInsertContentCommandProperties) => void;
	onPrepareImageUpdate?: (previousMediaFileId: string) => void;
	onSendContextReady?: SendContextReadyCallback;
	onStream?: StreamCallback;
	onStreamAbort?: () => void;
	onStreamEnd?: StreamEndCallback;
	onStreamError?: StreamErrorCallback;
	onStreamRepair?: StreamRepairCallback;
	onStreamStart?: StreamStartCallback;
	onStreamTimeout?: StreamTimeoutCallback;
	onTrackAIGeneratingMedia?: (fileId: string, collectionName?: string) => void;
	onUploadAndInsertMedia?: OnUploadAndInsertMediaCallback;
	reuploadImages?: (adfDoc?: DocNode) => Promise<void>;
	useGenericEditorSkill?: boolean;
};

/**
 * Base interface for all command handlers in the Rovo bridge system.
 * Command handlers are responsible for processing commands received from the Rovo bridge
 * and executing the appropriate operations in the editor.
 */
export interface ICommandHandler<TCommandProperties extends CommandProperties, TCommandResult> {
	/**
	 * This is called when the command handler is deregistered
	 * usually this means the command is no longer needed or it's re-registered
	 */
	deregister: () => void;

	/**
	 * Listens for and processes a command with the provided payload.
	 * This is the main entry point for command execution.
	 *
	 * @param payload - Command-specific payload to process
	 * @returns Promise or direct result depending on the command type
	 */
	execute: (payload: TCommandProperties) => Promise<TCommandResult> | TCommandResult;

	/**
	 * Updates the command handler props.
	 *
	 * @param props - The new props
	 */
	updateProps: (props: PassThroughCommandProps) => void;
}

/**
 * Interface for command handlers that support streaming operations.
 * Streaming handlers can be aborted mid-execution to stop processing
 * and clean up resources.
 */
export interface IStreamingCommandHandler {
	/**
	 * Aborts the current streaming operation.
	 * This should immediately stop processing, clean up any resources,
	 * and reset the handler to a ready state.
	 */
	abort: () => void;
}

/**
 * Abstract base class for all command handlers in the Rovo bridge system.
 * Provides common functionality and structure for handling commands from the Rovo bridge.
 *
 * @template TCommandProperties - The type of properties/parameters this handler expects
 * @template TCommandResult - The type of result this handler returns
 */
export abstract class CommandHandler<
	TCommandProperties extends CommandProperties,
	TCommandResult,
> implements ICommandHandler<TCommandProperties, TCommandResult> {
	/**
	 * Properties passed through from the command manager containing callbacks
	 * and utility functions for interacting with the editor and streaming system.
	 */
	protected props: PassThroughCommandProps;

	/**
	 * Creates a new command handler instance.
	 *
	 * @param props - Pass-through props containing callbacks and utilities
	 *                    needed for command execution and streaming operations
	 */
	constructor(props: PassThroughCommandProps) {
		this.props = props;
	}

	updateProps(props: PassThroughCommandProps): void {
		this.props = props;
	}

	deregister(): void {
		// do nothing
	}

	/**
	 * Abstract method that must be implemented by concrete command handlers.
	 * This method contains the core logic for processing the specific command type.
	 *
	 * @param payload - Command-specific parameters containing the data needed for execution
	 * @returns Promise that resolves with the command result when processing is complete
	 */
	abstract execute(payload: TCommandProperties): Promise<TCommandResult> | TCommandResult;
}
